import React from 'react';

type SsoProvider = 'Google' | 'SignInWithApple' | 'LoginWithAmazon';

const PROVIDER_LABELS: Record<SsoProvider, string> = {
    Google: 'Google',
    SignInWithApple: 'Apple',
    LoginWithAmazon: 'Amazon',
};

const PROVIDER_ICONS: Record<SsoProvider, React.ReactNode> = {
    Google: (
        <svg className="sso-icon" viewBox="0 0 24 24" width="18" height="18" aria-hidden="true">
            <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z"/>
            <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
            <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
            <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
        </svg>
    ),
    SignInWithApple: (
        <svg className="sso-icon" viewBox="0 0 24 24" width="18" height="18" aria-hidden="true">
            <path fill="currentColor" d="M12.152 6.896c-.948 0-2.415-1.078-3.96-1.04-2.04.027-3.91 1.183-4.961 3.014-2.117 3.675-.546 9.103 1.519 12.09 1.013 1.454 2.208 3.09 3.792 3.039 1.52-.065 2.09-.987 3.935-.987 1.831 0 2.35.987 3.96.948 1.637-.026 2.676-1.48 3.676-2.948 1.156-1.688 1.636-3.325 1.662-3.415-.039-.013-3.182-1.221-3.22-4.857-.026-3.04 2.48-4.494 2.597-4.559-1.429-2.09-3.623-2.324-4.39-2.376-2-.156-3.675 1.09-4.61 1.09zM15.53 3.83c.843-1.012 1.4-2.427 1.245-3.83-1.207.052-2.662.805-3.532 1.818-.78.896-1.454 2.338-1.273 3.714 1.338.104 2.715-.688 3.559-1.701"/>
        </svg>
    ),
    LoginWithAmazon: (
        <svg className="sso-icon" viewBox="0 0 24 24" width="18" height="18" aria-hidden="true">
            <path fill="none" stroke="#FF9900" strokeWidth="2" strokeLinecap="round" d="M3 14.5c5 3.6 12.5 3.8 17.5.4"/>
            <path fill="none" stroke="#FF9900" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" d="M17.2 13.4l3.6 1.2-.9 3.6"/>
        </svg>
    ),
};

/**
 * Providers enabled for this deployment, from REACT_APP_SSO_PROVIDERS
 * (comma-separated Cognito provider names). Defaults to Google only.
 */
const getEnabledProviders = (): SsoProvider[] => {
    const configured = (process.env.REACT_APP_SSO_PROVIDERS || 'Google')
        .split(',')
        .map(p => p.trim())
        .filter((p): p is SsoProvider => p in PROVIDER_LABELS);
    return configured.length > 0 ? configured : ['Google'];
};

interface SocialSignInButtonsProps {
    verb: string;
    disabled?: boolean;
    onError: (message: string) => void;
}

const SocialSignInButtons: React.FC<SocialSignInButtonsProps> = ({ verb, disabled, onError }) => {
    const handleSignIn = (provider: SsoProvider) => {
        const cognitoDomain = process.env.REACT_APP_COGNITO_DOMAIN;
        const oauthClientId = process.env.REACT_APP_COGNITO_OAUTH_CLIENT_ID;
        if (!cognitoDomain || !oauthClientId) {
            onError(`${PROVIDER_LABELS[provider]} Sign-In is not configured.`);
            return;
        }
        const redirectUri = `${window.location.origin}/login/callback`;
        const authorizeUrl = `${cognitoDomain}/oauth2/authorize?identity_provider=${provider}&response_type=code&client_id=${oauthClientId}&redirect_uri=${encodeURIComponent(redirectUri)}&scope=openid+email+profile`;
        window.location.href = authorizeUrl;
    };

    return (
        <div className="sso-buttons">
            {getEnabledProviders().map(provider => (
                <button
                    key={provider}
                    type="button"
                    className="sso-button"
                    onClick={() => handleSignIn(provider)}
                    disabled={disabled}
                >
                    {PROVIDER_ICONS[provider]}
                    {verb} {PROVIDER_LABELS[provider]}
                </button>
            ))}
        </div>
    );
};

export default SocialSignInButtons;
